import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ISlide, ISlideObject, Slide, SlidePlaylist } from '../../../shared/slide';
import { constants } from '../../../core/_constant/constants';
import { environment } from '../../../../environments/environment';
import { EventconfigStoreService } from '../../../shared/eventconfig-store.service';
import { SlideStoreService } from '../../../shared/slide-store.service';
import { PlayerStoreService } from '../../../shared/player-store.service';
import { ImageCacheService } from '../../../shared/image-cache-service.service';

@Component({
  selector: 'lse-slidelist-item',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush, // Add OnPush strategy
  template: `
    <div class='mini_div'
         [class.highlight]="selectedSlideObject?._id === playlistObject?._id"
         [style.--bg-color]="dominantColor">
      @defer (on viewport){
        <img
          (click)='playbackAtTimestamp(playlistObject?.videoTime)'
          class='mini_slide'
          [src]='thumbnailUrl'
          height='68'
          width='120'
        />
      } @placeholder (minimum 100ms){
        <img
          class='mini_slide'
          src='assets/images/placeholder/placeholder_200.webp'
          height='68'
          width='120'
        />
      }
    </div>
  `,
})
export class SlidelistItemComponent implements OnInit, OnChanges {
  @Input() playlistObject: SlidePlaylist;
  @Input() eventFolder: string;
  @Input() path: string;
  @Input() languages: string[];
  @Input() selectedLanguage: string;
  @Input() selectedSlide: ISlideObject;
  @Input() selectedSlideObject: SlidePlaylist;
  @Input() selectedSlideObjectIndex: number;

  @Output() vodEvent = new EventEmitter();
  @Output() centerSlide = new EventEmitter();
  @Output() showElement = new EventEmitter();
  @Output() playAtTimestamp = new EventEmitter();

  protected readonly constant = constants;
  protected readonly environment = environment;

  state: string;
  highlight = false;
  slideDirection: string;
  thumbnailUrl: string = '';

  eventConfigStoreService: EventconfigStoreService = inject(EventconfigStoreService);
  playerService: PlayerStoreService = inject(PlayerStoreService);
  slideStoreService: SlideStoreService = inject(SlideStoreService);
  imageCacheService: ImageCacheService = inject(ImageCacheService);
  dominantColor: string = 'rgba(240, 240, 240, 0.9)';

  ngOnInit(): void {
    this.getEventState();
    this.slideDirection = this.eventConfigStoreService.eState === 'live' ? 'rtl' : 'ltr';
    this.updateThumbnailUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only update thumbnail URL when necessary inputs change
    if (changes['playlistObject'] ||
      changes['selectedLanguage'] ||
      changes['languages'] ||
      changes['eventFolder']) {
      this.updateThumbnailUrl();
    }
  }

  getEventState() {
    this.state = this.eventConfigStoreService.getEventState();
  }

  updateThumbnailUrl(): void {
    if (!this.playlistObject?.slide) {
      this.thumbnailUrl = '';
      this.dominantColor = 'rgba(240, 240, 240, 0.9)';
      return;
    }

    const slideId = this.playlistObjectSource();
    if (!slideId) {
      this.thumbnailUrl = '';
      this.dominantColor = 'rgba(240, 240, 240, 0.9)';
      return;
    }

    this.thumbnailUrl = `${this.environment.apiUrl}/event/${this.eventFolder}/${this.constant.SLIDES_FOLDER_PATH}/${slideId}_200.webp`;

    // Get cached color or use default
    this.dominantColor = this.imageCacheService.getDominantColor(this.thumbnailUrl);

    // Preload image and when it loads, extract color if not already cached
    this.imageCacheService.preloadImage(this.thumbnailUrl).then(img => {
      if (img && !this.imageCacheService.hasColor(this.thumbnailUrl)) {
        this.dominantColor = this.imageCacheService.extractAndCacheDominantColor(img, this.thumbnailUrl);
      }
    });
  }
  playbackAtTimestamp(timestamp: number) {
    if (!timestamp  || this.eventConfigStoreService.eState === 'live' ) return;

    this.playerService.setTime(timestamp);
    this.slideStoreService.setSelectedSlide(this.playlistObject);
  }

  slideTCtoSeconds(tc: string): number {
    if (!tc) return 0;

    let slideTimeCodeArray = tc.split(':');
    return (Number.parseInt(slideTimeCodeArray[0]) * 60 +
        Number.parseInt(slideTimeCodeArray[1])) * 60 +
      Number.parseInt(slideTimeCodeArray[2]);
  }

  private playlistObjectSource(): null | string {
    if (!this.playlistObject) return null;

    // First try: Check selected language
    if (this.selectedLanguage && this.playlistObject.slide[this.selectedLanguage]) {
      return this.playlistObject.slide[this.selectedLanguage];
    }

    // Second try: Check first language from languages array
    if (this.languages?.length > 0 && this.playlistObject.slide[this.languages[0]]) {
      return this.playlistObject.slide[this.languages[0]];
    }

    // Third try: Check second language from languages array
    if (this.languages?.length > 1 && this.playlistObject.slide[this.languages[1]]) {
      return this.playlistObject.slide[this.languages[1]];
    }

    // A slide can belong to a language which is no longer configured on the
    // event. Still render it instead of leaving the history entry blank.
    return Object.values(this.playlistObject.slide)[0] ?? null;
  }
}
